import { LoadingButton } from '@mui/lab'
import { Box, Stack, Tab, Tabs } from '@mui/material'
import { getOrganizationalUsers } from 'api/organization'
import { deleteUser, getUsers } from 'api/users'
import { useAuthContext } from 'components/context/AuthProvider'
import { AddAdminModal } from 'components/modals/AddAdminModal'
import ConfirmRemoveModal from 'components/modals/ConfirmRemoveModal'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import { getTitle } from 'components/tabs/TabsTitle'
import { getActionColumn } from 'features/UI'
import { useUsersPageFilter } from 'features/User/hooks/useUsersPageFilter'
import { useExportCSV } from 'hooks/useExportCSV'
import { useGridControl } from 'hooks/useGridControl'
import { useRefreshTable } from 'hooks/useRefreshTable'
import { useRemoveModalControl } from 'hooks/useRemoveModalControl'
import { useShowControl } from 'hooks/useShowControl'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { routes } from 'routes/routes'
import { BaseTableRequest, UserEditTab, UserOrgTab, UserTypeTab } from 'types'
import { getRoute, useGetDataSource } from 'utils'
import { InviteTraineeModal } from '../InviteTraineeModal/InviteTraineeModal'
import { csvOrgUserFields, csvTraineeFields, getUserColumnDefs } from './columnDefs'

const selectedTabsForActions = [UserTypeTab.TRAINEE, UserTypeTab.CAREGIVER, UserTypeTab.ADMIN]
const analystForbiddenFields = [
  'firstname',
  'lastname',
  'email',
  'phoneNumber',
  'userBasicInfo.phoneNumber',
  'userBasicInfo.street',
  'userBasicInfo.city',
  'userBasicInfo.dateOfBirth',
]

export const UsersPage = () => {
  const navigate = useNavigate()
  const { isAnalist } = useAuthContext()

  const {
    filter,
    CaregiverStatusFilter,
    CohortsFilter,
    OrgUserTagsFilter,
    RoleFilter,
    StatusFilter,
    OrgStateUnitFilter,
    UserStatusFilter,
    TraineeStatusFilter,
    selectedTab,
    handleChangeSelectedTab,
  } = useUsersPageFilter()

  const getUsersWrapper = useCallback(
    (params: BaseTableRequest) => {
      const method = selectedTab === UserTypeTab.ORG ? getOrganizationalUsers : getUsers

      return method({
        ...params,
        filter: {
          ...params.filter,
          ...filter,
        },
      })
    },
    [selectedTab, filter],
  )

  const getDataSource = useGetDataSource(getUsersWrapper)

  const { gridApi, columnApi, onGridReady } = useGridControl(getDataSource)

  const left = (
    <>
      <Tabs value={selectedTab} onChange={handleChangeSelectedTab} indicatorColor="primary">
        <Tab value={UserTypeTab.TRAINEE} label={getTitle('Trainees')} />
        <Tab value={UserTypeTab.CAREGIVER} label={getTitle('Family Caregivers')} />
        <Tab value={UserTypeTab.ADMIN} label={getTitle('Admins')} />
        <Tab value={UserTypeTab.ORG} label={getTitle('Organizational Users')} />
      </Tabs>
    </>
  )

  const handleEditUser = useCallback(
    (userId: number) => {
      const route = getRoute(routes.userDetails, { userId, tab: UserEditTab.Details })
      navigate(route)
    },
    [navigate],
  )

  const handleEditOrgUser = useCallback(
    (userId: number) => {
      const route = getRoute(routes.adminOrgUserDetails, { userId, tab: UserOrgTab.Details })
      navigate(route)
    },
    [navigate],
  )

  const { idToRemove, openDeleteModal, closeDeleteModal, removeLoading, handleConfirmRemove } =
    useRemoveModalControl({
      deleteFunc: deleteUser,
      successCallback: () => gridApi?.purgeInfiniteCache(),
      warning: "Can't remove user",
    })

  const actions = useMemo(() => {
    if (selectedTab === UserTypeTab.ADMIN) {
      return [
        {
          name: 'Edit',
          onClick: handleEditUser,
        },
      ]
    }

    return [
      {
        name: 'Edit',
        onClick: handleEditUser,
      },
      {
        name: 'Delete',
        onClick: openDeleteModal,
      },
    ]
  }, [handleEditUser, openDeleteModal, selectedTab])

  const orgUserActions = useMemo(() => {
    return [
      {
        name: 'Edit',
        onClick: handleEditOrgUser,
      },
    ]
  }, [handleEditOrgUser])

  const columnDefs = useMemo(() => {
    const defs = getUserColumnDefs(selectedTab).slice()

    if (isAnalist) {
      return [...defs.filter((item) => !analystForbiddenFields.includes(item.field || ''))]
    }

    if (selectedTabsForActions.includes(selectedTab)) {
      defs.push(getActionColumn(actions))
    } else if (selectedTab === UserTypeTab.ORG) {
      defs.push(getActionColumn(orgUserActions))
    }

    return defs
  }, [actions, isAnalist, orgUserActions, selectedTab])

  useRefreshTable({ params: { selectedTab }, gridApi })

  const { csvLoading, onExportCSV } = useExportCSV(
    getUsersWrapper,
    gridApi,
    columnApi,
    selectedTab === UserTypeTab.TRAINEE ? csvTraineeFields : csvOrgUserFields,
    selectedTab === UserTypeTab.TRAINEE ? 'Trainees.csv' : 'Organizational Users.csv',
  )

  const showAddUser = selectedTab === UserTypeTab.ADMIN && !isAnalist

  const handleOpenAddAdmin = () => setIsOpenAddAdmin(true)

  const handleCloseAddAdmin = () => {
    setIsOpenAddAdmin(false)
    gridApi?.purgeInfiniteCache()
  }

  const [isOpenAddAdmin, setIsOpenAddAdmin] = useState(false)

  const [isInviteOpen, onOpenInvite, onCloseInvite] = useShowControl()

  const showInvite = selectedTab === UserTypeTab.TRAINEE && !isAnalist

  return (
    <>
      <TableToolbar
        left={left}
        exportCSVEnable={selectedTab === UserTypeTab.TRAINEE || selectedTab === UserTypeTab.ORG}
        csvLoading={csvLoading}
        onExportCSV={onExportCSV}
        onAddUser={showAddUser ? handleOpenAddAdmin : undefined}
      >
        {showInvite && (
          <LoadingButton onClick={onOpenInvite} variant="contained" sx={{ minWidth: 120 }}>
            Invite
          </LoadingButton>
        )}
      </TableToolbar>

      {selectedTab === UserTypeTab.TRAINEE && (
        <Stack flexDirection="row" alignItems="center" mt={1} mb={2} className="gap-16">
          <Box minWidth="220px">{CohortsFilter}</Box>
          <Box minWidth="200px">{TraineeStatusFilter}</Box>
          {OrgStateUnitFilter}
        </Stack>
      )}

      {selectedTab === UserTypeTab.CAREGIVER && (
        <Stack flexDirection="row" alignItems="center" mt={1} mb={2} className="gap-16">
          <Box minWidth="220px">{CohortsFilter}</Box>
          <Box minWidth="200px">{CaregiverStatusFilter}</Box>
        </Stack>
      )}

      {selectedTab === UserTypeTab.ORG && (
        <Stack flexDirection="row" alignItems="center" mt={1} mb={2} className="gap-16">
          <Box minWidth="200px">{RoleFilter}</Box>
          <Box minWidth="200px">{UserStatusFilter}</Box>
          <Box minWidth="220px">{OrgUserTagsFilter}</Box>
          {OrgStateUnitFilter}
        </Stack>
      )}

      {selectedTab === UserTypeTab.ADMIN && (
        <Stack flexDirection="row" alignItems="center" mt={1} mb={2} className="gap-16">
          <Box minWidth="200px">{RoleFilter}</Box>
          <Box minWidth="200px">{StatusFilter}</Box>
        </Stack>
      )}

      <InfinityAgGrid pagination columnDefs={columnDefs} onGridReady={onGridReady} />

      <ConfirmRemoveModal
        entityToRemove="User"
        loading={removeLoading}
        isOpen={idToRemove !== null}
        handleConfirm={handleConfirmRemove}
        handleClose={closeDeleteModal}
      />

      <AddAdminModal isOpen={isOpenAddAdmin} handleClose={handleCloseAddAdmin} />

      <InviteTraineeModal
        isAdmin
        isOpen={isInviteOpen}
        handleClose={onCloseInvite}
        onInvited={() => gridApi?.purgeInfiniteCache()}
      />
    </>
  )
}
